import { ReactElement, useCallback, useMemo } from 'react'
import { ColorRepresentation, Vector3 } from 'three'
import shallow from 'zustand/shallow'
import { useTheme } from '@mui/material'
import { toImmutable } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import { useModelStore } from 'src/components/pages/Editor/stores/modelStore'
import { useSceneStore } from 'src/components/pages/Editor/stores/sceneStore'
import MemberMeshElement from '../MemberMeshElement'
import SimpleCylinderMesh from '../SimpleCylinderMesh'

const defaultPositionThickness = 0.15

interface Props {
  data: Position
  height?: number
  width?: number
  position_type: 'rip' | 'lintel'
  outlines?: boolean
  onClick?: (event: ThreeEvent<MouseEvent>) => void
  isSelected?: boolean
  isHighlighted?: boolean
  isTarget?: boolean
  visible?: boolean
  checkPosition?: number
  crossSectionShape?: SteelCSShape | RectangularCSShape
}

const PositionMeshElement = ({
  data,
  height,
  width,
  position_type,
  onClick,
  checkPosition,
  isSelected = false,
  isHighlighted = false,
  isTarget = false,
  visible = true,
  crossSectionShape,
}: Props): ReactElement => {
  const theme = useTheme()

  const color = useMemo(() => {
    if (isHighlighted) return theme.scenePalette.selection
    if (isTarget) return theme.scenePalette.elements3d.targets

    // Check if this is a steel cross-section
    const isSteel = crossSectionShape?.kind_literal === 'SteelCS'
    return isSteel ? theme.scenePalette.elements3d.steelBeams : undefined
  }, [
    isHighlighted,
    isTarget,
    crossSectionShape,
    theme.scenePalette.elements3d.targets,
    theme.scenePalette.elements3d.steelBeams,
    theme.scenePalette.selection,
  ])

  const outlinesColor = useMemo(
    () => (isSelected ? theme.scenePalette.highlightDark : theme.scenePalette.outlines),
    [isSelected, theme.scenePalette.highlightDark, theme.scenePalette.outlines],
  )
  const lineWidth = useMemo(() => (isSelected ? 2 : 1), [isSelected])

  // state picking due to performance reasons
  const mappedColor = useSceneStore(
    useCallback(state => state.guidToColor[data.position_guid], [data.position_guid]),
  ) as ColorRepresentation | undefined

  // Hooks for transparency
  const translucent = useModelStore(state => state.translucent)
  const { visibilityByType: transparencyVisibilityByType, opacity: transparencyOpacity } =
    useModelStore(state => state.transparencyVisibilityModel, shallow)

  const getTransparencyOpacity = useMemo(() => {
    if (!translucent) return 1
    const elementType = position_type === 'rip' ? 'rips' : 'lintels'
    const isTransparentForType = transparencyVisibilityByType[elementType]
    return isTransparentForType ? transparencyOpacity : 1
  }, [translucent, transparencyVisibilityByType, transparencyOpacity, position_type])

  const getPositionVectorFromCheckPosition = (checkPosition: number) => {
    const length = data.start.distanceTo(data.end)
    const absolutePosition = length * checkPosition

    // Rips only go in the z direction
    if (position_type === 'rip')
      return new Vector3(data.start.x, data.start.y, data.start.z + absolutePosition)

    // Lintels can go in x or y direction depending on orientation of the wall
    if (data.start.x !== data.end.x)
      return new Vector3(data.start.x + absolutePosition, data.start.y, data.start.z)
    else return new Vector3(data.start.x, data.start.y + absolutePosition, data.start.z)
  }

  return (
    <>
      <MemberMeshElement
        name={data.position_guid}
        start={data.start}
        end={data.end}
        coordinateSystem={data.coordinate_system}
        userData={{
          guid: data.position_guid,
          elementType: position_type,
          snapGeometry: 'centerLine',
          start: data.start,
          end: data.end,
        }}
        onClick={onClick}
        cursor={onClick ? 'pointer' : undefined}
        color={mappedColor || color}
        outlinesColor={outlinesColor}
        lineWidth={lineWidth}
        height={height || defaultPositionThickness}
        width={width || defaultPositionThickness}
        visible={visible}
        outlines={true}
        crossSectionShape={crossSectionShape}
        opacity={getTransparencyOpacity}
      />
      {isSelected && checkPosition != null && visible && (
        <SimpleCylinderMesh
          position={toImmutable(getPositionVectorFromCheckPosition(checkPosition))}
          isActive={true}
          color="red"
          maxScale={7}
          onClick={() => {}}
          disableDepthTest={true}
        />
      )}
    </>
  )
}

export default PositionMeshElement
